import React, { useState, useEffect } from 'react';
import { 
  FileCode, Sparkles, Wand2, Plus, Trash2, Search, Loader2, Save, X, 
  ChevronRight, Brain, Terminal, ShieldAlert 
} from 'lucide-react';
import { toast } from 'sonner';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { 
  Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle 
} from "@/components/ui/sheet";
import { 
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue 
} from "@/components/ui/select";
import { useAuth } from "@/integrations/supabase/AuthProvider";
import { supabase } from "@/integrations/supabase/client";
import { cn } from "@/lib/utils";

const PossessionsSheet = ({ open, onOpenChange }) => {
  const { user, profile } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isAdding, setIsAdding] = useState(false);
  const [search, setSearch] = useState("");
  
  const [newItem, setNewItem] = useState({
    title: "",
    type: "script",
    content: ""
  });

  const isPro = profile?.is_pro === true;
  const limit = isPro ? 100 : 10;

  const fetchItems = async () => {
    if (!user) return;
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('possessions')
        .select('*')
        .order('created_at', { ascending: false });
      
      if (error) throw error;
      setItems(data || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) fetchItems();
  }, [open, user]);

  const handleCreate = async () => {
    if (!newItem.title || !newItem.content) {
      toast.error("Identity and core logic are required.");
      return;
    }

    if (items.length >= limit) {
      toast.error(`Possession limit reached (${limit}). Upgrade or remove old items.`);
      return;
    }

    try {
      const { data, error } = await supabase
        .from('possessions')
        .insert([{ ...newItem, user_id: user.id }])
        .select()
        .single();
      
      if (error) throw error;
      
      setItems([data, ...items]);
      setIsAdding(false);
      setNewItem({ title: "", type: "script", content: "" });
      toast.success("Possession archived successfully.");
    } catch (err) {
      toast.error("Sync error.");
    }
  };

  const handleDelete = async (id) => {
    try {
      const { error } = await supabase.from('possessions').delete().eq('id', id);
      if (error) throw error;
      setItems(items.filter(item => item.id !== id));
      toast.info("Possession de-archived.");
    } catch (err) {
      toast.error("Deletion error.");
    }
  };

  const filteredItems = items.filter(item => 
    item.title.toLowerCase().includes(search.toLowerCase()) || 
    item.type.toLowerCase().includes(search.toLowerCase())
  );

  const getIcon = (type) => {
    switch(type) {
      case 'script': return FileCode;
      case 'prompt': return Sparkles;
      case 'skill': return Brain;
      default: return Wand2;
    }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="glass-surface h-full border-white/5 bg-sonas-bg p-0 text-sonas-text w-full sm:max-w-md overflow-hidden flex flex-col" side="right">
        <SheetHeader className="p-6 border-b border-white/5 bg-sonas-bg/50 backdrop-blur-xl">
          <div className="flex items-center justify-between">
            <div>
              <SheetTitle className="text-2xl font-syne flex items-center gap-2">
                My Possessions <span className="text-[10px] font-bold uppercase bg-sonas-accent/10 text-sonas-accent px-2 py-0.5 rounded-full">Beta</span>
              </SheetTitle>
              <SheetDescription className="text-sonas-muted">Archive and apply reusable architectural logic.</SheetDescription>
            </div>
            <div className="text-right">
              <p className="text-[10px] font-bold uppercase tracking-widest text-sonas-dim">Vault Capacity</p>
              <p className={cn("text-xs font-bold", items.length >= limit ? "text-red-500" : "text-sonas-accent")}>
                {items.length}/{limit}
              </p>
            </div>
          </div>
        </SheetHeader>

        <div className="flex-1 overflow-y-auto p-6 sonas-scrollbar">
          {isAdding ? (
            <div className="space-y-6 animate-in fade-in slide-in-from-top-4 duration-300 bg-white/[0.02] p-6 rounded-3xl border border-white/5">
              <div className="space-y-4">
                <div className="space-y-2">
                  <label className="text-[10px] font-bold uppercase tracking-widest text-sonas-dim">Item Identity</label>
                  <Input 
                    value={newItem.title} 
                    onChange={e => setNewItem({...newItem, title: e.target.value})}
                    placeholder="e.g. Master Knit Controller"
                    className="bg-sonas-surface2 border-white/5 h-11 rounded-xl"
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-[10px] font-bold uppercase tracking-widest text-sonas-dim">Category</label>
                  <Select value={newItem.type} onValueChange={val => setNewItem({...newItem, type: val})}>
                    <SelectTrigger className="bg-sonas-surface2 border-white/5 h-11 rounded-xl">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-sonas-bg border-white/10">
                      <SelectItem value="script">Luau Script</SelectItem>
                      <SelectItem value="prompt">AI Prompt</SelectItem>
                      <SelectItem value="skill">Engine Skill</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <label className="text-[10px] font-bold uppercase tracking-widest text-sonas-dim">Content Logic</label>
                  <Textarea 
                    value={newItem.content} 
                    onChange={e => setNewItem({...newItem, content: e.target.value})} 
                    placeholder="Paste the script or prompt instruction here..."
                    className="bg-sonas-surface2 border-white/5 min-h-[200px] rounded-xl text-xs font-mono"
                  />
                </div>
              </div>
              <div className="flex gap-3">
                <Button onClick={() => setIsAdding(false)} variant="ghost" className="flex-1 h-11 rounded-xl">Abort</Button>
                <Button onClick={handleCreate} className="flex-1 h-11 bg-sonas-accent text-sonas-bg font-bold rounded-xl shadow-lg shadow-sonas-accent/20">Archive</Button>
              </div>
            </div>
          ) : (
            <div className="space-y-6">
              <div className="flex gap-3">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-sonas-dim" />
                  <Input 
                    placeholder="Search vault..." 
                    value={search}
                    onChange={e => setSearch(e.target.value)}
                    className="pl-10 bg-sonas-surface2 border-white/5 h-11 rounded-xl"
                  />
                </div>
                <Button 
                  onClick={() => setIsAdding(true)}
                  className="h-11 w-11 rounded-xl bg-sonas-accent text-sonas-bg"
                  size="icon"
                >
                  <Plus size={20} />
                </Button>
              </div>

              <div className="space-y-3">
                {loading ? (
                  <div className="py-20 flex justify-center">
                    <Loader2 className="h-6 w-6 animate-spin text-sonas-dim" />
                  </div>
                ) : filteredItems.length === 0 ? (
                  <div className="py-20 text-center space-y-4 opacity-30">
                    <Terminal className="h-10 w-10 mx-auto" />
                    <p className="text-[10px] font-bold uppercase tracking-widest">Vault Empty</p>
                  </div>
                ) : (
                  filteredItems.map(item => {
                    const Icon = getIcon(item.type);
                    return (
                      <div key={item.id} className="group relative bg-white/[0.02] border border-white/5 p-4 rounded-2xl hover:border-sonas-accent/30 transition-all">
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center gap-3">
                            <div className="h-8 w-8 rounded-lg bg-sonas-accent/10 flex items-center justify-center text-sonas-accent">
                              <Icon size={16} />
                            </div>
                            <div>
                              <p className="text-sm font-bold">{item.title}</p>
                              <p className="text-[9px] text-sonas-dim uppercase tracking-tighter">{item.type}</p>
                            </div>
                          </div>
                          <button 
                            onClick={() => handleDelete(item.id)}
                            className="h-8 w-8 rounded-lg flex items-center justify-center text-sonas-dim hover:bg-red-500/10 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-all"
                          >
                            <Trash2 size={14} />
                          </button>
                        </div>
                        <div className="bg-black/20 p-3 rounded-xl border border-white/5 max-h-[100px] overflow-hidden relative">
                          <pre className="text-[10px] font-mono text-sonas-muted truncate">{item.content}</pre>
                          <div className="absolute inset-0 bg-gradient-to-t from-sonas-bg/40 to-transparent" />
                        </div>
                        <Button 
                          variant="ghost" 
                          className="w-full mt-3 h-8 text-[10px] font-bold uppercase tracking-widest text-sonas-accent hover:bg-sonas-accent/5"
                          onClick={() => toast.success("Copied to logic buffer.")}
                        >
                          Copy to Prompt
                        </Button>
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          )}
        </div>

        <div className="p-6 bg-sonas-bg border-t border-white/5">
          <section className="bg-sonas-accent/5 border border-sonas-accent/20 p-4 rounded-2xl flex items-start gap-4">
            <ShieldAlert className="h-5 w-5 text-sonas-accent shrink-0 mt-0.5" />
            <p className="text-[10px] leading-relaxed text-sonas-muted">
              <span className="font-bold text-sonas-accent">SYSTEM NOTE:</span> Items stored in your possessions are indexed by the architect to improve context recall during heavy system generation.
            </p>
          </section>
        </div>
      </SheetContent>
    </Sheet>
  );
};

export default PossessionsSheet;